// frontend/src/components/trades/SignalLessons.tsx
//
// Performance page: which causes keep showing up behind wins and losses,
// counted from every closed trade's review (backend tradeReview.ts), for the
// engine's auto-tracked signals or for your own trades.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@heroui/react";
import { Lightbulb } from "lucide-react";
import { EmptyState, Panel, Segmented } from "@/components/kit";
import { cn } from "@/lib/utils";
import { formatR } from "../chart/chartSignals";
import type { TradeSource, TradeStats } from "../desk/deskData";

interface LessonRow { key: string; text: string; lost: number; won: number; even: number }

const SOURCES = [
  { id: "auto", label: "Engine signals" },
  { id: "manual", label: "Your trades" },
] as const;

export function SignalLessons({ className }: { className?: string }) {
  const [source, setSource] = useState<TradeSource>("auto");
  const [record, setRecord] = useState<Record<TradeSource, TradeStats> | null>(null);
  const [lessons, setLessons] = useState<{ closed: number; reasons: LessonRow[] } | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLessons(null);
    Promise.all([
      fetch("/api/tracked-trades/summary?period=all").then(r => r.json()),
      fetch(`/api/tracked-trades/lessons?period=all&source=${source}`).then(r => r.json()),
    ])
      .then(([sum, les]) => {
        if (cancelled) return;
        if (sum.bySource) setRecord(sum.bySource);
        setLessons(les.success ? { closed: les.closed, reasons: les.reasons } : { closed: 0, reasons: [] });
        setError(false);
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [source]);

  const st = record?.[source];
  const max = Math.max(1, ...(lessons?.reasons ?? []).map(r => r.lost + r.won + r.even));

  return (
    <Panel
      className={className}
      title="Why trades won or lost"
      description="Counted from each closed trade's review"
      actions={<Segmented aria-label="Whose trades" options={SOURCES} value={source} onChange={setSource} />}
    >
      {error ? (
        <p className="text-sm text-muted">Couldn't load the reviews.</p>
      ) : !lessons ? (
        <Skeleton className="h-32 rounded-lg" />
      ) : (
        <div className="space-y-3 text-sm">
          {st && (
            <p className="text-muted">
              <span className="font-medium text-foreground">{source === "auto" ? "Engine signals" : "Your trades"}:</span>{" "}
              {st.won} won · {st.lost} lost{st.even > 0 ? ` · ${st.even} even` : ""}
              {st.winRate != null && <> · <span className="num">{Math.round(st.winRate)}%</span> win rate</>}
              {st.won + st.lost + st.even > 0 && (
                <> · <span className={cn("num", st.netR > 0.05 ? "text-profit" : st.netR < -0.05 ? "text-loss" : "")}>{formatR(st.netR)}</span></>
              )}
              {source === "auto" && " · tracked automatically, practice only"}
            </p>
          )}

          {lessons.reasons.length === 0 ? (
            <EmptyState
              icon={Lightbulb}
              title="No reviewed trades yet"
              description="A review is written a few minutes after each trade closes."
            />
          ) : (
            <ul className="space-y-2">
              {lessons.reasons.slice(0, 8).map(r => {
                const total = r.lost + r.won + r.even;
                return (
                  <li key={r.key} className="grid grid-cols-[minmax(0,1fr)_7rem] items-center gap-3">
                    <div className="min-w-0">
                      <div className="truncate">{r.text}</div>
                      <div className="mt-1 flex h-1.5 overflow-hidden rounded-full bg-default" aria-hidden>
                        <span className="bg-loss" style={{ width: `${(r.lost / max) * 100}%` }} />
                        <span className="bg-profit" style={{ width: `${(r.won / max) * 100}%` }} />
                        <span className="bg-muted/50" style={{ width: `${(r.even / max) * 100}%` }} />
                      </div>
                    </div>
                    <div className="num text-right text-xs text-muted">
                      {r.lost > 0 && <span className="text-loss">{r.lost} lost</span>}
                      {r.lost > 0 && r.won > 0 && " · "}
                      {r.won > 0 && <span className="text-profit">{r.won} won</span>}
                      {r.even > 0 && <>{r.lost + r.won > 0 && " · "}{r.even} even</>}
                      <span className="sr-only"> of {total}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-xs text-muted">
            {lessons.closed > 0 && <>From {lessons.closed} reviewed trade{lessons.closed === 1 ? "" : "s"}. </>}
            Open any trade's <Link to="/trades" className="text-brand-text underline underline-offset-2">Why?</Link> to read its full story.
          </p>
        </div>
      )}
    </Panel>
  );
}
